import React, { memo, Fragment, useEffect, useCallback, useMemo } from "react";
import pluralize from "pluralize";

import AxiomSpinner from "__AxiomComponents/AxiomUI/AxiomSpinner/AxiomSpinner";
import { useAxiomState } from "__AxiomComponents/AxiomHooks/AxiomHooks";

import HawkeyeV2Post from "../HawkeyeV2Post/HawkeyeV2Post";
import HawkeyeV2PostComments from "../HawkeyeV2PostComments/HawkeyeV2PostComments";
import HawkeyeV2PostHeading from "../HawkeyeV2PostHeading/HawkeyeV2PostHeading";

import "./HawkeyeV2Posts.scss";

function HawkeyeV2Posts(props) {
  const {
    widget,
    conversations,
    projectID,
    isLoading,
    onAddPost,
    allCollaborators,
    onEditPost,
    onMarkAsCompleted,
  } = props;

  const [state, dispatch] = useAxiomState(
    {
      expandedPost: null,
      isLoadingComments: false,
      comments: [],
      filters: {
        selectedCollaborator: "All",
        isDueThisWeek: false,
        isAllConversations: true,
        isOpenConversations: false,
        isCloseConversations: false,
      },
    },
    props.appName,
    props.appID,
    "HawkeyeV2Posts",
    props.componentID
  );

  useEffect(() => {
    if (state.expandedPost) {
      dispatch((state) => ({
        isLoadingComments: true,
        ...(state.comments.length ? { comments: [] } : {}),
      }));
      dispatch(
        {
          postID: state.expandedPost.id,
          projectID: projectID,
        },
        "AxiomEvent",
        "getComments"
      );
    }
  }, [dispatch, state.expandedPost, projectID]);

  const toggleExpandPost = useCallback(
    (post) => {
      dispatch((state) => (state.expandedPost ? { expandedPost: null } : { expandedPost: post }));
    },
    [dispatch]
  );

  const onAddComment = useCallback(
    (commentMessage, sendEmail) => {
      dispatch(
        (state) => ({
          postID: state.expandedPost.id,
          projectID: projectID,
          commentMessage: commentMessage,
          sendEmail: sendEmail,
          dashboardName: widget.pagename,
          widgetID: widget.subscribeID,
        }),
        "AxiomEvent",
        "addComment"
      );
    },
    [dispatch, projectID, widget.pagename, widget.subscribeID]
  );

  const setFilters = useCallback(
    (filters) => {
      dispatch({
        filters,
      });
    },
    [dispatch]
  );

  const topFilteredConversations = useMemo(() => {
    let newConversations = conversations;
    if (state.filters.isDueThisWeek) {
      const daysToadd = 7 - new Date().getDay();
      const lastDate = new Date(new Date().setDate(new Date().getDate() + daysToadd));
      newConversations = newConversations.filter(
        (d) => new Date(d.dueDate).getTime() <= lastDate.getTime()
      );
    }
    if (state.filters.selectedCollaborator !== "All") {
      newConversations = newConversations.filter(
        (d) => d.assignTo === state.filters.selectedCollaborator
      );
    }
    return newConversations;
  }, [state.filters, conversations]);

  const filteredConversations = useMemo(() => {
    let newConversations = topFilteredConversations;
    if (state.filters.isOpenConversations) {
      newConversations = newConversations.filter((d) => !d.isCompleted);
    }
    if (state.filters.isCloseConversations) {
      newConversations = newConversations.filter((d) => d.isCompleted);
    }
    return newConversations;
  }, [state.filters, topFilteredConversations]);

  const collaborators = useMemo(() => {
    if (allCollaborators && allCollaborators.length > 0) {
      let collaborators = [...allCollaborators];
      collaborators = collaborators.map((d) => ({
        label: d.label,
        value: d.userID,
      }));
      collaborators.unshift({
        label: "All",
        value: "All",
      });
      return collaborators;
    } else {
      return [];
    }
  }, [allCollaborators]);

  return (
    <div className="hawkeye-conversations-posts">
      <HawkeyeV2PostHeading
        conversations={topFilteredConversations}
        onAddPost={onAddPost}
        filters={state.filters}
        setFilters={setFilters}
        allCollaborators={collaborators}
      />
      {isLoading && (
        <div className="hawkeye-conversations-fetching">
          <AxiomSpinner spinnerWidth={24} />
          Fetching conversations...
        </div>
      )}
      <div className="hawkeye-conversations-post-list nice-scroll">
        {state.expandedPost ? (
          <Fragment>
            <HawkeyeV2Post
              id={state.expandedPost.id}
              post={state.expandedPost}
              toggleExpandPost={toggleExpandPost}
              onEditPost={onEditPost}
              onMarkAsCompleted={onMarkAsCompleted}
            />
            <HawkeyeV2PostComments
              isFetchingComments={state.isLoadingComments}
              post={state.expandedPost}
              comments={state.comments}
              postDescription={state.expandedPost.description}
              onAddComment={onAddComment}
            />
          </Fragment>
        ) : filteredConversations.length > 0 ? (
          filteredConversations.map((c) => (
            <HawkeyeV2Post
              id={c.id}
              post={c}
              toggleExpandPost={toggleExpandPost}
              onEditPost={onEditPost}
              onMarkAsCompleted={onMarkAsCompleted}
            />
          ))
        ) : (
          <div className="hawkeye-empty-conversations">No Conversation found </div>
        )}
      </div>
    </div>
  );
}

export default memo(HawkeyeV2Posts);
